import "./trax-modal.css";

import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import {
	Dialog,
	DialogActionsBar,
} from "@tql/ui-components-react/kendo/dialogs";
import { Checkbox } from "@tql/ui-components-react/kendo/inputs";
import { xIcon } from "@tql/ui-components-react/kendo/svg-icons";
import { Button } from "@tql/ui-components-react/tql/buttons";
import type { ComponentProps } from "react";
import { useEffect, useRef, useState } from "react";

export type TraxModalType = "primary" | "error" | "success" | "confirmation";

export type TraxModalProps = ComponentProps<typeof Dialog> & {
	readonly children: React.ReactNode;
	readonly type?: TraxModalType;
	readonly confirmText?: string;
	readonly cancelText?: string;
	readonly onConfirm?: () => void;
	readonly onCancel?: () => void;
	readonly confirmVariant?:
		| "primary"
		| "secondary"
		| "tertiary"
		| "danger"
		| "dangerSecondary"
		| "dangerTertiary";
	readonly showConfirmationButtons?: boolean;
	/** Render the confirm button before Cancel (DOM and focus order, not just visually). */
	readonly confirmFirst?: boolean;
	readonly showConfirmButton?: boolean;
	readonly showCancelButton?: boolean;
	readonly showButtonsDisabled?: boolean;
	readonly confirmDisabled?: boolean;
	readonly showGoBackButton?: boolean;
	readonly onGoBack?: () => void;
	readonly skipPreference?: {
		readonly label: string;
		readonly storageKey: string;
	};
	readonly canLeave?: boolean;
	readonly canLeaveText?: string;
	readonly onCanLeave?: () => void;
};

function getModalClassName(modalType: TraxModalType): string {
	switch (modalType) {
		case "error": {
			return "trax-modal-error";
		}
		case "success": {
			return "trax-modal-success";
		}
		case "confirmation": {
			return "trax-modal-confirmation";
		}
		case "primary": {
			return "trax-modal-primary";
		}
		default: {
			return "trax-modal-primary";
		}
	}
}

export function TraxModal({
	children,
	type = "primary",
	confirmText = "Confirm",
	cancelText = "Cancel",
	onConfirm,
	onCancel,
	confirmVariant = "primary",
	showConfirmationButtons,
	confirmFirst = false,
	showConfirmButton = true,
	showCancelButton = true,
	showButtonsDisabled = false,
	confirmDisabled = false,
	showGoBackButton = false,
	canLeave = false,
	canLeaveText,
	onGoBack,
	skipPreference,
	onCanLeave,
	...dialogProps
}: Readonly<TraxModalProps>) {
	const [skipChecked, setSkipChecked] = useState(false);

	const sentinelRef = useRef<HTMLDivElement>(null);

	const isAlreadySkipped = useRef(
		skipPreference
			? globalThis.localStorage.getItem(skipPreference.storageKey) === "true"
			: false,
	);

	const onConfirmRef = useRef(onConfirm);
	onConfirmRef.current = onConfirm;

	useEffect(() => {
		if (isAlreadySkipped.current) {
			onConfirmRef.current?.();
		}
	}, []);

	useEffect(() => {
		sentinelRef.current?.focus();
	}, []);

	if (isAlreadySkipped.current) {
		return null;
	}

	const handleSkipChange = (value: boolean) => {
		if (!skipPreference) return;
		setSkipChecked(value);
	};

	const className =
		`trax-modal ${getModalClassName(type)} ${dialogProps.className || ""}`.trim();

	const isConfirmationModal =
		type === "confirmation" || showConfirmationButtons;

	const handleConfirm = () => {
		if (skipPreference && skipChecked) {
			globalThis.localStorage.setItem(skipPreference.storageKey, "true");
		}
		onConfirm?.();
	};

	const handleCancel = () => {
		onCancel?.();
	};

	const cancelButton = showCancelButton ? (
		<Button
			disabled={showButtonsDisabled}
			onClick={handleCancel}
			variant={"secondary"}
		>
			{cancelText}
		</Button>
	) : null;

	const confirmButton = showConfirmButton ? (
		<Button
			disabled={showButtonsDisabled || confirmDisabled}
			onClick={handleConfirm}
			variant={confirmVariant}
		>
			{confirmText}
		</Button>
	) : null;

	const handleClose = () => {
		if (onCancel) {
			onCancel();
		} else if (dialogProps.onClose) {
			const syntheticEvent = new Event("close");
			const mockDialogEvent = {
				syntheticEvent,
				nativeEvent: syntheticEvent,
				target: null,
				isDefaultPrevented: () => false,
				preventDefault: () => {},
			} as unknown as Parameters<NonNullable<typeof dialogProps.onClose>>[0];
			dialogProps.onClose(mockDialogEvent);
		}
	};

	return (
		<Dialog {...dialogProps} className={className}>
			{/* Empty div to catch initial focus and prevent focus from going to the modal buttons */}
			<div ref={sentinelRef} style={{ outline: "none" }} tabIndex={-1} />
			<div className={"trax-modal-children"}>{children}</div>
			{skipPreference ? (
				<div className={"trax-modal-skip-preference"}>
					<Checkbox
						checked={skipChecked}
						id={"trax-modal-skip-preference-checkbox"}
						label={skipPreference.label}
						onChange={(e) => {
							handleSkipChange(e.value);
						}}
					/>
				</div>
			) : null}
			{isConfirmationModal ? (
				<DialogActionsBar>
					<div className={"trax-modal-confirmation-actions"}>
						{canLeave ? (
							<Button
								disabled={showButtonsDisabled}
								onClick={onCanLeave}
								variant={"tertiary"}
							>
								{canLeaveText}
							</Button>
						) : null}
						{confirmFirst ? (
							<>
								{confirmButton}
								{cancelButton}
							</>
						) : (
							<>
								{cancelButton}
								{confirmButton}
							</>
						)}

						{showGoBackButton ? (
							<Button
								disabled={showButtonsDisabled}
								onClick={() => {
									if (onGoBack) {
										onGoBack();
									} else {
										globalThis.history.back();
									}
								}}
								variant={"secondary"}
							>
								Go Back
							</Button>
						) : null}
					</div>
				</DialogActionsBar>
			) : null}
			{dialogProps.title ? null : (
				<div className={"trax-modal-button trax-modal-close-button"}>
					<Button onClick={handleClose} variant={"tertiary"}>
						<SvgIcon icon={xIcon} size={"large"}></SvgIcon>
					</Button>
				</div>
			)}
		</Dialog>
	);
}
